import axios from 'axios'
import {
  ListResponse,
  SimpleResponse,
} from '../interfaces/Shipment/ListResponseDto'
import {
  businessUnitMapper,
  buLocationDjangoToBUByLocationDto,
  mapBUDtoToBUInterface,
  mapBusinessUnitDtoListToBusinessUnitInterface,
  serviceDjangoToServiceDTO,
  storeDjangoToBUInterface,
} from '../utils/mappers'
import {
  Nullable,
  BusinessUnitDTO,
  BusinessUnitInterface,
  BusinessUnitLocationDjangoDto,
  ServiceDTO,
  ServiceDjangoDto,
  PaymentModeId,
  DeliveryEnum,
  StoreDjangoDto,
} from '../interfaces'
import { BusinessUnitDTOV2 } from '../interfaces/BusinessUnit/BusinessUnitInterface'
import { BUByLocationDto } from '../interfaces/Location/LocationByBu'
import { apiDjango } from './ApiDjango'

const { REACT_APP_URL_PATH_API, REACT_APP_URL_PATH_API_DJANGO } = process.env

// Carga masiva (API Django). GET /api/store — listado de tiendas. Lanza en caso de fallo
// para que el thunk del sub-slice lo capture. Nota: el DTO Django no trae buid ni buTypeID;
// dedupe/filtra por buCode aguas abajo (ver businessUnitOptions en ShipmentBulkUpload).
export const getStoresDjango = async (
  params?: Record<string, unknown>
): Promise<BusinessUnitInterface[]> => {
  try {
    const resp = await apiDjango.get(`${REACT_APP_URL_PATH_API_DJANGO}/store`, {
      params,
    })
    if (resp.status === 200 && resp.data && resp.data.error === false) {
      const list = (resp.data.data ?? []) as StoreDjangoDto[]
      return list.map(storeDjangoToBUInterface)
    }
    console.error('getStoresDjango: respuesta con error', {
      code: resp.data?.code,
      message: resp.data?.message,
    })
    throw new Error(resp.data?.message || 'No se pudieron obtener las tiendas')
  } catch (error) {
    console.error('getStoresDjango: fallo', { error })
    throw error
  }
}

// Carga masiva (API Django). GET /api/store/coverage?latitude=&longitude= — tienda que
// cubre unas coordenadas. Drop-in de getBusinessUnitByTerritory: misma firma
// (longitude, latitude) y mismo retorno SimpleResponse<BusinessUnitInterface>. NO lanza.
// `signal` permite abortar (Review dispara hasta 200 llamadas; cancelar en cleanup).
export const getStoreByCoverageDjango = async (
  longitude: number,
  latitude: number,
  signal?: AbortSignal
): Promise<SimpleResponse<BusinessUnitInterface>> => {
  try {
    const resp = await apiDjango.get(
      `${REACT_APP_URL_PATH_API_DJANGO}/store/coverage`,
      { params: { latitude, longitude }, signal }
    )
    if (resp.status === 200 && resp.data && resp.data.error === false) {
      const store = resp.data.data as StoreDjangoDto | null
      return {
        model: store ? storeDjangoToBUInterface(store) : null,
        didError: false,
        message: resp.data.message ?? '',
        errorMessage: '',
      }
    }
    const errMessage =
      resp.data?.message ||
      resp.data?.error_data?.error ||
      'No se pudo obtener la tienda de cobertura del punto especificado'
    return {
      model: null,
      didError: true,
      message: errMessage,
      errorMessage: errMessage,
    }
  } catch (error) {
    // Aborto de la peticion (cleanup del useEffect): no es un error de negocio.
    if ((error as any)?.name === 'CanceledError' || (error as any)?.code === 'ERR_CANCELED') {
      return {
        model: null,
        didError: true,
        message: 'Consulta de cobertura cancelada',
        errorMessage: 'Consulta de cobertura cancelada',
      }
    }
    console.error('getStoreByCoverageDjango: fallo', { longitude, latitude, error })
    return {
      model: null,
      didError: true,
      message: 'No se pudo obtener la tienda de cobertura del punto especificado',
      errorMessage:
        'No se pudo obtener la tienda de cobertura del punto especificado',
    }
  }
}

export const getBusinessUnits = async (): Promise<
  Nullable<BusinessUnitInterface[]>
> => {
  const uri = `${REACT_APP_URL_PATH_API}/BusinessUnit/BusinessUnitV2?status=1&IsNational=false&excludeOperationLocked=true`
  try {
    const response = await axios.get(uri)
    if (response.status === 200) {
      const storeDto = response.data
      var datamap = storeDto?.map((dto: BusinessUnitDTO) =>
        businessUnitMapper(dto)
      )
      return datamap
    }
  } catch (error) {}
  return null
}

export const getBusinessUnitsType = async (
  type: number
): Promise<Nullable<BusinessUnitInterface[]>> => {
  const uri = `${REACT_APP_URL_PATH_API}/BusinessUnit/BusinessUnitV2?status=1&IsNational=false&excludeOperationLocked=true`
  try {
    const response = await axios.get(uri)
    if (response.status === 200) {
      const storeDto = response.data
      var stoteDtoBu = storeDto as BusinessUnitDTOV2[]
      stoteDtoBu = stoteDtoBu.filter(
        (x) => x.buTypeID?.toString() === type.toString()
      )
      var datamap = stoteDtoBu?.map((dto: BusinessUnitDTO) =>
        businessUnitMapper(dto)
      )
      return datamap
    }
  } catch (error) {}
  return null
}

export const getBusinessUnitsPolly = async (): Promise<
  Nullable<BusinessUnitInterface[]>
> => {
  const uri = `${REACT_APP_URL_PATH_API}/BusinessUnit/BusinessUnitV2?status=1&IsNational=false&withPolygonsList=true&excludeOperationLocked=true`
  try {
    const response = await axios.get(uri)
    if (response.status === 200) {
      const storeDto = response.data
      var datamap = storeDto?.map((dto: BusinessUnitDTO) =>
        businessUnitMapper(dto)
      )
      return datamap.filter((bu: BusinessUnitInterface) => bu.buCode != '8110')
    }
  } catch (error) {}

  return null
}

export const getBusinessUnitByTerritory = async (
  longitude: number,
  latitude: number
): Promise<SimpleResponse<BusinessUnitInterface>> => {
  const uri = `${REACT_APP_URL_PATH_API}/BusinessUnit/GetBusinessUnitByTerritory?longitude=${longitude}&latitude=${latitude}`
  try {
    const response = await axios.get(uri)

    if (response.status === 200) {
      const storeDto = response.data.model
      if (storeDto != null) {
        response.data.model = mapBUDtoToBUInterface(storeDto)
      }
      return response.data
    }
  } catch (error) {}

  return {
    model: null,
    didError: true,
    message: 'No se pudo obtener la tienda de cobertura del punto especificado',
    errorMessage:
      'No se pudo obtener la tienda de cobertura del punto especificado',
  }
}

export const getBusinessUnitsInnovus = async (): Promise<
  Nullable<BusinessUnitInterface[]>
> => {
  const uri = `${REACT_APP_URL_PATH_API}/BusinessUnit/BusinessUnitV2?status=1&withPolygonsList=false&IsNational=false&includePlatforms=true`
  try {
    const response = await axios.get(uri)

    if (response.status === 200) {
      const storeDto = response.data
      return mapBusinessUnitDtoListToBusinessUnitInterface(storeDto)
    }
  } catch (error) {}

  return null
}

// ---------------------------------------------------------------------------
// Servicios y relacion ciudad↔tienda (API Django). Los tres getters de servicios son
// drop-in de sus equivalentes .NET de abajo: misma firma y mismo retorno
// ListResponse<ServiceDTO[]>, para que los consumidores solo cambien el import.
// Envelope Django: { code, error, message, data, error_data } (HTTP 200 salvo 403).
// ---------------------------------------------------------------------------
async function serviceListDjango(
  path: string,
  params: Record<string, unknown>,
  errMsg: string
): Promise<ListResponse<ServiceDTO[]>> {
  try {
    const resp = await apiDjango.get(
      `${REACT_APP_URL_PATH_API_DJANGO}${path}`,
      { params }
    )
    if (resp.status === 200 && resp.data && resp.data.error === false) {
      const list = (resp.data.data ?? []) as ServiceDjangoDto[]
      return {
        model: list.map(serviceDjangoToServiceDTO),
        didError: false,
        message: resp.data.message ?? '',
        errorMessage: '',
      }
    }
    const m = resp.data?.message || errMsg
    console.error(`serviceListDjango: respuesta con error en ${path}`, {
      code: resp.data?.code,
      message: resp.data?.message,
    })
    return { model: null, didError: true, message: m, errorMessage: m }
  } catch (error) {
    const m = (error as any)?.response?.data?.message || errMsg
    console.error(`serviceListDjango: fallo ${path}`, { error })
    return { model: null, didError: true, message: m, errorMessage: m }
  }
}

/** GET /api/service/by-payment-mode — servicios activos por modalidad de pago. */
export const getServicesFromPaymentModeDjango = (
  paymentModeID: PaymentModeId
): Promise<ListResponse<ServiceDTO[]>> =>
  serviceListDjango(
    '/service/by-payment-mode',
    { payment_mode_id: paymentModeID },
    'No se pudieron obtener los servicios'
  )

/**
 * GET /api/service/by-delivery-type — servicios activos por tipo de entrega.
 * `isInternational` solo acepta 0/1 (no true/false); omitirlo devuelve nacionales +
 * internacionales.
 */
export const getServicesFromDeliveryTypeDjango = (
  deliveryTypeID: DeliveryEnum,
  isInternational?: 0 | 1
): Promise<ListResponse<ServiceDTO[]>> =>
  serviceListDjango(
    '/service/by-delivery-type',
    {
      delivery_type_id: deliveryTypeID,
      ...(isInternational === undefined
        ? {}
        : { is_international: isInternational }),
    },
    'No se pudieron obtener los servicios'
  )

/** GET /api/service/locked-by-application — servicios bloqueados para una aplicacion. */
export const getAppLockedServicesDjango = (
  applicationID: number
): Promise<ListResponse<ServiceDTO[]>> =>
  serviceListDjango(
    '/service/locked-by-application',
    { application_id: applicationID },
    'No se pudieron obtener los servicios bloqueados'
  )

/**
 * GET /api/business-unit-location — relacion ciudad↔tienda (la tabla que la carga masiva
 * usa para resolver la tienda de una ciudad). Lanza en fallo para que el thunk de
 * bulkCatalogs lo capture, igual que getStoresDjango.
 */
export const getBusinessUnitLocationsDjango = async (params?: {
  bu_code?: string
  bu_id?: number
}): Promise<BUByLocationDto[]> => {
  try {
    const resp = await apiDjango.get(
      `${REACT_APP_URL_PATH_API_DJANGO}/business-unit-location`,
      { params }
    )
    if (resp.status === 200 && resp.data && resp.data.error === false) {
      const list = (resp.data.data ?? []) as BusinessUnitLocationDjangoDto[]
      return list.map(buLocationDjangoToBUByLocationDto)
    }
    console.error('getBusinessUnitLocationsDjango: respuesta con error', {
      code: resp.data?.code,
      message: resp.data?.message,
    })
    throw new Error(
      resp.data?.message || 'No se pudo obtener la relacion ciudad/tienda'
    )
  } catch (error) {
    console.error('getBusinessUnitLocationsDjango: fallo', { error })
    throw error
  }
}

export const getServicesFromPaymentMode = async (
  paymentModeID: PaymentModeId
): Promise<ListResponse<ServiceDTO[]>> => {
  const uri = `${REACT_APP_URL_PATH_API}/getServicesFromPaymentMode?PaymentModeID=${paymentModeID}`

  try {
    const response = await axios.get(uri)

    if (response.status === 200) {
      return response.data
    }
  } catch (error) {}

  return {
    model: null,
    didError: true,
    message: 'No se pudieron obtener los servicios',
    errorMessage: 'No se pudieron obtener los servicios',
  }
}

export const getServicesFromDeliveryType = async (
  deliveryTypeID: DeliveryEnum
): Promise<ListResponse<ServiceDTO[]>> => {
  const uri = `${REACT_APP_URL_PATH_API}/GetServicesFromDeliveryType?DeliveryTypeID=${deliveryTypeID}`

  try {
    const response = await axios.get(uri)

    if (response.status === 200) {
      return response.data
    }
  } catch (error) {}

  return {
    model: null,
    didError: true,
    message: 'No se pudieron obtener los servicios',
    errorMessage: 'No se pudieron obtener los servicios',
  }
}

export const getLockedServicesByTier = async (
  tierID: number
): Promise<ListResponse<ServiceDTO[]>> => {
  const uri = `${REACT_APP_URL_PATH_API}/GetLockedServicesByTier?TierID=${tierID}`

  try {
    const response = await axios.get(uri)

    if (response.status === 200) {
      return response.data
    }
  } catch (error) {}

  return {
    model: null,
    didError: true,
    message: 'No se pudieron obtener los servicios bloqueados',
    errorMessage: 'No se pudieron obtener los servicios bloqueados',
  }
}

export const getLockBUServices = async (
  buCode: string
): Promise<ListResponse<ServiceDTO[]>> => {
  const uri = `${REACT_APP_URL_PATH_API}/BusinessUnit/GetLockBUServices?BUCode=${buCode}`

  try {
    const response = await axios.get(uri)

    if (response.status === 200) {
      return response.data
    }
  } catch (error) {}

  return {
    model: null,
    didError: true,
    message: 'No se pudieron obtener los servicios bloqueados',
    errorMessage: 'No se pudieron obtener los servicios bloqueados',
  }
}

export const getAppLockedServices = async (
  applicationID: number
): Promise<ListResponse<ServiceDTO[]>> => {
  const uri = `${REACT_APP_URL_PATH_API}/BusinessUnit/GetAppLockedServices?ApplicationID=${applicationID}`

  try {
    const response = await axios.get(uri)

    if (response.status === 200) {
      return response.data
    }
  } catch (error) {}

  return {
    model: null,
    didError: true,
    message: 'No se pudieron obtener los servicios bloqueados',
    errorMessage: 'No se pudieron obtener los servicios bloqueados',
  }
}

export const getService = async (
  serviceID: number
): Promise<ListResponse<ServiceDTO>> => {
  const uri = `${REACT_APP_URL_PATH_API}/GetService?ServiceID=${serviceID}`

  try {
    const response = await axios.get(uri)

    if (response.status === 200) {
      return response.data
    }
  } catch (error) {}

  return {
    model: null,
    didError: true,
    message: 'No se pudo obtener el servicio',
    errorMessage: 'No se pudo obtener el servicio',
  }
}
